/**
 * Prebook Thunks
 *
 * Async thunks for flight prebook operations.
 * Error popup handling is centralized in popupsSlice via extraReducers.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { PrebookRequestParams, PrebookResponse } from '@contracts';
import {
  prebookFlight as prebookFlightApi,
  PrebookError,
  type PrebookErrorDetails,
} from '../api/prebookApi';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId, getUUID } from '@/shared/utils';

// ============================================
// Constants
// ============================================

/**
 * Popup key for prebook error - kept for backward compatibility with ReviewPopup
 * @deprecated Use API_ERROR_POPUP_KEY from popupsSlice for centralized error handling
 */
export const PREBOOK_ERROR_POPUP_KEY = 'PREBOOK_ERROR';

// ============================================
// Reject payload (string or structured error details for popup)
// ============================================

export type PrebookRejectValue = string | PrebookErrorDetails;

// ============================================
// Thunks
// ============================================

/**
 * Prebook a flight
 *
 * On error with errorDetails (e.g., invalid coupon), rejects with structured
 * error details. The centralized popupsSlice handles showing the error popup.
 */
export const prebookFlightThunk = createAsyncThunk<
  PrebookResponse,
  PrebookRequestParams,
  { rejectValue: PrebookRejectValue }
>('flights/prebook', async (params, { rejectWithValue }) => {
  try {
    logger.warn('[FlightsThunk] Calling prebook API');
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();
    const response = await prebookFlightApi({ ...params, sessionId, userId });
    logger.warn('[FlightsThunk] Prebook successful:', response);
    return response;
  } catch (error) {
    logger.error('[FlightsThunk] Prebook failed:', error);
    // Return structured error details for centralized popup handling
    if (error instanceof PrebookError && error.errorDetails) {
      return rejectWithValue(error.errorDetails);
    }
    if (error instanceof PrebookError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to prebook flight');
  }
});
